import { useMemo, useCallback } from "react";
import type {
  ObjectsAdvancedFilterProps,
  ControlledObjectsAdvancedFilterProps
} from "./types";
import { ObjectFilterGroup } from "./ObjectFilterGroup";
import { createDefaultFilter, createFieldMapping } from "./utils";
import { useObjectTypeTranslationsManager } from "@certa/common/store/dataObjects";
import styles from "./ObjectsAdvancedFilter.module.css";

export const ObjectsAdvancedFilter = ({
  value,
  onChange,
  selectedObjectType,
  fieldOptions,
  operatorsByFieldType,
  enhancementRules,
  disabled = false,
  className,
  shouldEnableGroupQuery,
  hideSmartColumns = false
}: ObjectsAdvancedFilterProps) => {
  const { getObjectTypeByTag } = useObjectTypeTranslationsManager();
  const objectTypeData = getObjectTypeByTag(selectedObjectType);

  const fieldMapping = useMemo(() => {
    return createFieldMapping(fieldOptions);
  }, [fieldOptions]);

  const handleRootGroupReset = useCallback(() => {
    const emptyFilter = {
      type: "all" as const,
      conditions: []
    };
    onChange(emptyFilter);
  }, [onChange]);

  return (
    <div className={`${styles.container} ${className || ""}`}>
      <ObjectFilterGroup
        group={value}
        fieldOptions={fieldOptions}
        fieldMapping={fieldMapping}
        onChange={onChange}
        onRemove={handleRootGroupReset}
        isRootGroup={true}
        shouldEnableGroupQuery={shouldEnableGroupQuery}
        disabled={disabled || !objectTypeData}
        objectTypeData={objectTypeData}
        selectedObjectType={selectedObjectType}
        operatorsByFieldType={operatorsByFieldType}
        enhancementRules={enhancementRules}
        hideSmartColumns={hideSmartColumns}
      />
    </div>
  );
};

export const ControlledObjectsAdvancedFilter = ({
  value,
  onChange = (_value: ObjectsAdvancedFilterProps["value"]) => {},
  selectedObjectType,
  fieldOptions,
  operatorsByFieldType,
  enhancementRules,
  disabled = false,
  className,
  shouldEnableGroupQuery = true
}: ControlledObjectsAdvancedFilterProps) => {
  const defaultValue: ObjectsAdvancedFilterProps["value"] = useMemo(
    () => createDefaultFilter(selectedObjectType),
    [selectedObjectType]
  );

  const handleChange = useCallback(
    (newValue: ObjectsAdvancedFilterProps["value"]) => {
      onChange(newValue);
    },
    [onChange]
  );

  return (
    <ObjectsAdvancedFilter
      value={value || defaultValue}
      onChange={handleChange}
      selectedObjectType={selectedObjectType}
      fieldOptions={fieldOptions}
      disabled={disabled}
      className={className}
      shouldEnableGroupQuery={shouldEnableGroupQuery}
      operatorsByFieldType={operatorsByFieldType}
      enhancementRules={enhancementRules}
    />
  );
};
